import { duplicate } from './duplicate.ts';
import {
  type Predicate,
  type StreamTransform,
  type Tap,
  type Transform,
} from './types.ts';

export type Mapper<A, B> = (input: AsyncIterable<A>) => AsyncIterable<B>;
export type SyncTransform<T, V> = (t: T) => V;

export function pipe<A, B>(input: A, mapper: SyncTransform<A, B>): B;
export function pipe<A, B, C>(
  input: A,
  mapper: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
): C;
export function pipe<A, B, C, D>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
): D;
export function pipe<A, B, C, D, E>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
): E;
export function pipe<A, B, C, D, E, F>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
): F;
export function pipe<A, B, C, D, E, F, G>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
): G;
export function pipe<A, B, C, D, E, F, G, H>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
  mapper7: SyncTransform<G, H>,
): H;
export function pipe<A, B, C, D, E, F, G, H, I>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
  mapper7: SyncTransform<G, H>,
  mapper8: SyncTransform<H, I>,
): I;
export function pipe<A, B, C, D, E, F, G, H, I, J>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
  mapper7: SyncTransform<G, H>,
  mapper8: SyncTransform<H, I>,
  mapper9: SyncTransform<I, J>,
): J;
export function pipe<A, B, C, D, E, F, G, H, I, J, K>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
  mapper7: SyncTransform<G, H>,
  mapper8: SyncTransform<H, I>,
  mapper9: SyncTransform<I, J>,
  mapper10: SyncTransform<J, K>,
): K;
export function pipe<A, B, C, D, E, F, G, H, I, J, K, L>(
  input: A,
  mapper1: SyncTransform<A, B>,
  mapper2: SyncTransform<B, C>,
  mapper3: SyncTransform<C, D>,
  mapper4: SyncTransform<D, E>,
  mapper5: SyncTransform<E, F>,
  mapper6: SyncTransform<F, G>,
  mapper7: SyncTransform<G, H>,
  mapper8: SyncTransform<H, I>,
  mapper9: SyncTransform<I, J>,
  mapper10: SyncTransform<J, K>,
  mapper11: SyncTransform<K, L>,
): L;
export function pipe(
  input: AsyncIterable<any>,
  ...mappers: Array<(input: any) => any>
): AsyncIterable<any> | Promise<any> {
  return mappers.reduce((prev, mapper) => mapper(prev), input);
}

export function map<T, V>(transform: Transform<T, V>): Mapper<T, V> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<V> {
    for await (const item of iterator) {
      yield await transform(item);
    }
  };
}

export function sleep(time: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, time);
  });
}

export function filter<T>(predicate: Predicate<T>): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    for await (const item of iterator) {
      if (await predicate(item)) {
        yield item;
      }
    }
  };
}

/**
 * Splits an async iterable stream into two based on a predicate, applying different
 * transformations to each split stream.
 *
 * This function is crucial for efficient stream processing when you need to apply
 * different operations to elements based on a condition. It avoids iterating over the
 * source stream multiple times, which is especially beneficial for large or
 * resource-intensive streams. Use `fork` with `pipe` to create branching logic in your
 * stream processing pipelines while maintaining performance.
 *
 * @param predicate A function that takes a stream element and returns a boolean.
 * @param ifTrue A transformation to apply to elements that satisfy the predicate.
 * @param ifFalse A transformation to apply to elements that do not satisfy the
 * predicate.
 * @param maxBufferLength The maximum number of elements to buffer before waiting for
 * the other stream to consume some values. Smaller values reduce memory usage but can
 * reduce performance if one stream reads much faster than the other. Defaults to 50.
 *
 * @example
 * // Split a stream of numbers, doubling those > 5 and incrementing the rest
 * const [largeNumbers, smallNumbers] = await pipe(
 *   sourceStream,
 *   fork(
 *     (x) => x > 5,
 *     (trueStream) => pipe(trueStream, map((x) => x * 2), toArray()),
 *     (falseStream) => pipe(falseStream, map((x) => x + 1), toArray())
 *   )
 * );
 * // largeNumbers is an array of numbers > 5, each doubled
 * // smallNumbers is an array of numbers <= 5, each incremented by 1
 */
export function fork<T, U = AsyncIterable<T>, V = AsyncIterable<T>>(
  predicate: Predicate<T>,
  ifTrue: SyncTransform<AsyncIterable<T>, U>,
  ifFalse: SyncTransform<AsyncIterable<T>, V>,
  maxBufferLength = 50,
): (iterator: AsyncIterable<T>) => [U, V] {
  return function (iterator: AsyncIterable<T>): [U, V] {
    const [iterator1, iterator2] = duplicate(iterator, maxBufferLength);

    const makeIterator = <X>(
      predicateValue: boolean,
      source: AsyncIterable<T>,
      mapper: SyncTransform<AsyncIterable<T>, X>,
    ) =>
      mapper(
        filter<T>(async (value) => (await predicate(value)) === predicateValue)(source),
      );

    return [
      makeIterator(true, iterator1, ifTrue),
      makeIterator(false, iterator2, ifFalse),
    ];
  };
}

export function tap<T>(action: Tap<T>): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    for await (const item of iterator) {
      yield item;
      await action(item);
    }
  };
}

export function take<T>(count: number | undefined): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    let taken = 0;

    for await (const item of iterator) {
      if (count != null && taken >= count) {
        return;
      }

      yield item;
      taken++;
    }
  };
}

export function timeout<T>(timeoutMs: number): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    const deadline = Date.now() + timeoutMs;

    for await (const item of iterator) {
      yield item;

      if (Date.now() > deadline) {
        return;
      }
    }
  };
}

export function takeWhile<T>(predicate: Predicate<T>): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    for await (const item of iterator) {
      if (!predicate(item)) {
        return;
      }

      yield item;
    }
  };
}

export function takeUntil<T>(
  transform: (value: T) => number,
  threshold: number | undefined,
): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    let total = 0;

    for await (const item of iterator) {
      const size = transform(item);
      if (threshold != null && total + size > threshold) {
        return;
      }
      total += size;
      yield item;
    }
  };
}

export function skip<T>(count: number): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    const taken = 0;

    for await (const item of iterator) {
      if (taken < count) {
        continue;
      }

      yield item;
    }
  };
}

export function flatMap<T, V>(transform: StreamTransform<T, V>): Mapper<T, V> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<V> {
    for await (const page of iterator) {
      const transformedPage = await transform(page);

      for await (const item of transformedPage) {
        yield item;
      }
    }
  };
}

export function throttle<T>(time: number): Mapper<T, T> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T> {
    const originalTimestamp = Date.now();
    let iterationNumber = 0;
    for await (const value of iterator) {
      yield value;

      const startWindow = originalTimestamp + iterationNumber * time;
      const passed = Date.now() - startWindow;

      if (passed < time) {
        await sleep(time - passed);
      }

      iterationNumber++;
    }
  };
}

export function chunk<T>(count: number): Mapper<T, T[]> {
  return async function* (iterator: AsyncIterable<T>): AsyncIterable<T[]> {
    let window = [];
    for await (const value of iterator) {
      window.push(value);
      if (window.length >= count) {
        yield window;
        window = [];
      }
    }

    if (window.length > 0) {
      yield window;
    }
  };
}

export function count<T>(): SyncTransform<AsyncIterable<T>, Promise<number>> {
  return async function (iterator: AsyncIterable<T>): Promise<number> {
    let count = 0;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for await (const _ of iterator) {
      count++;
    }

    return count;
  };
}

/**
 * Returns the first element of the iterator, or `undefined` if the iterator is empty.
 *
 * @remarks This does NOT consume the entire iterator. **Do not use this if there are
 * side effects on the entire iterator that need to be run** (e.g. `tap()`).
 */
export function first<T>(): SyncTransform<AsyncIterable<T>, Promise<T | undefined>> {
  return async function (iterator: AsyncIterable<T>): Promise<T | undefined> {
    for await (const item of iterator) {
      return item;
    }

    return undefined;
  };
}

/**
 * Returns the first element of the iterator, or `undefined` if the iterator is empty.
 * This will consume the entire iterator and is safe to use if there are side effects.
 */
export function firstConsumeAll<T>(): SyncTransform<
  AsyncIterable<T>,
  Promise<T | undefined>
> {
  return async function (iterator: AsyncIterable<T>): Promise<T | undefined> {
    let isFirstIteration = true;
    let firstElement: T | undefined;

    // It's important that we don't return early here, as we need to consume the entire
    // iterator. This is because the iterator might have side effects via `tap()` which
    // would otherwise be missed.
    for await (const item of iterator) {
      if (isFirstIteration) {
        firstElement = item;
        isFirstIteration = false;
      }
    }

    return firstElement;
  };
}

export function last<T>(): SyncTransform<AsyncIterable<T>, Promise<T | undefined>> {
  return async function (iterator: AsyncIterable<T>): Promise<T | undefined> {
    let lastElement: T | undefined;

    for await (const item of iterator) {
      lastElement = item;
    }

    return lastElement;
  };
}

export function toPromise<T>(): SyncTransform<AsyncIterable<T>, Promise<T | undefined>> {
  return first();
}

export function toArray<T>(): SyncTransform<AsyncIterable<T>, Promise<T[]>> {
  return async function (iterator: AsyncIterable<T>): Promise<T[]> {
    const items = [];

    for await (const item of iterator) {
      items.push(item);
    }

    return items;
  };
}

export function toMap<T extends string, V>(): SyncTransform<
  AsyncIterable<[T, V]>,
  Promise<Map<T, V>>
> {
  return async function (iterator: AsyncIterable<[T, V]>): Promise<Map<T, V>> {
    const map = new Map<T, V>();

    for await (const [key, value] of iterator) {
      map.set(key, value);
    }

    return map;
  };
}
